import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {RoutingService} from '@app/service/routing/routing.service';
import {LoggerService} from '@app/service/logger/logger.service';
import {
  Login,
  LinkAnonToEmail,
  GoogleLogin,
  FacebookLogin,
  LoginFailure, LinkAnonAcctToFacebook, LinkAnonAcctToGoogle
} from '@shared/state/auth/auth.actions';
import {Actions, ofActionCompleted, Store} from '@ngxs/store';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {Meta, Title} from '@angular/platform-browser';
import {AuthState} from '@shared/state/auth/auth.state';
import {setSEOInfo} from '@shared/helpers/seo';
import {faFacebookF} from '@fortawesome/free-brands-svg-icons';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', './../auth.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  messages: string[];
  returnUrl: string;
  submitted: boolean;
  loading = false;
  loginForm: FormGroup;
  passwordVisible = false;
  redirectLink;
  faFacebook = faFacebookF;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder, private route: ActivatedRoute, private logger: LoggerService,
              private routingService: RoutingService, private store: Store, private title: Title, private meta: Meta,
              private activatedRoute: ActivatedRoute, private actions: Actions) {
    const pageName = 'Login to Carvry';
    const desc = 'Login to explore top vehicles and saved searches you have made previously';
    setSEOInfo(this.title, this.meta, pageName, desc);

    this.messages = [];
    this.returnUrl = this.route.snapshot.queryParams.returnUrl || '/explore';
    this.submitted = false;
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
    this.resolveNavParams();

    this.actions.pipe(ofActionCompleted(LoginFailure))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.submitted = false;
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  resolveNavParams() {
    this.activatedRoute.queryParams
      .pipe(take(1))
      .subscribe(params => {
        if (!params) {
          return;
        }
        const redirectLink = params.redirectTo;
        if (redirectLink) {
          this.redirectLink = redirectLink;
        }
      });
  }

  get f() {
    return this.loginForm.controls;
  }

  login(): any {
    this.messages = [];
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      this.submitted = false;
      return;
    }

    this.loading = true;
    const data = this.loginForm.value;
    const shouldLinkAnon = this.store.selectSnapshot(AuthState.getIsAnonymous);

    if (shouldLinkAnon) {
      return this.store.dispatch(new LinkAnonToEmail({
        email: data.email,
        password: data.password,
        linkType: 'login',
        redirectLink: this.redirectLink
      }));
    } else {
      return this.store.dispatch(new Login({
        email: data.email,
        password: data.password,
        redirectLink: this.redirectLink
      }));
    }
  }

  loginWithGoogle() {
    this.loading = true;
    const shouldLinkAnon = this.store.selectSnapshot(AuthState.getIsAnonymous);
    if (shouldLinkAnon) {
      this.store.dispatch(new LinkAnonAcctToGoogle({
        linkType: 'login',
        redirectLink: this.redirectLink
      }));
    } else {
      this.store.dispatch(new GoogleLogin({
        linkType: 'login',
        redirectLink: this.redirectLink
      }));
    }
  }

  loginWithFacebook() {
    this.loading = true;
    const shouldLinkAnon = this.store.selectSnapshot(AuthState.getIsAnonymous);
    if (shouldLinkAnon) {
      this.store.dispatch(new LinkAnonAcctToFacebook({
        linkType: 'login',
        redirectLink: this.redirectLink
      }));
    } else {
      this.store.dispatch(new FacebookLogin({
        linkType: 'login',
        redirectLink: this.redirectLink
      }));
    }
  }
}
